import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { toastr } from 'react-redux-toastr'
import _ from 'lodash'
import classnames from 'classnames'
import './Files.css'
import {Download, Upload} from 'lucide-react'
import { defaultT } from '../utils/i18n'
import { Table, TrashCell, AutoSizer, Column } from './DataTable'
import moment from "moment"
const ProgressBar = ({ percentage }) => (
    <div className="progress-bar-container">
        <div className={classnames('progress-bar-fill', { 'upload-processing': percentage === 100 })} style={{ width: `${percentage}%` }} />
    </div>
)
import axios from 'axios'
import {SortDirection} from 'react-virtualized/dist/commonjs/Table'
import FilePreviewAndDownload from "./filePreviewAndDownload";
import UploadHint from './UploadHint'
import { isTooLarge } from '../utils/uploadLimits'

const sort = (ids, getValueForId, sortDirection) =>
    _.orderBy(
        ids,
        getValueForId,
        sortDirection === SortDirection.ASC ? 'asc' : 'desc'
    )

class Files extends Component {
    state = {
        sortBy: 'date',
        sortDirection: 'DESC',
        uploadState: {}, // Track per-file progress
        hovered: false,
        isDragging: false,
    }

    handleResponse(data, t) {
        const { push, user, anonymous } = this.props
        if (data) {
            const userObject = !!anonymous ? {} : {user}
            push({
                ...data,
                ...userObject,
                date: moment().format('YYYY-MM-DD HH:mm')
            })
        }
        else {
            toastr.error(t('errorUploading'))
        }
    }

    async handleOneFileUpload(file, t, allFiles){
        // Add file type validation
        const { allowedFormats } = this.props
        if (allowedFormats && allowedFormats.length > 0) {
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
            if (!allowedFormats.some(format => format.toLowerCase() === fileExtension)) {
                toastr.error(t('unsupportedFileType', {
                    allowedFormats: allowedFormats.join(', ')
                }))
                return
            }
        }

        if (await isTooLarge(file, 'file')) {
            toastr.error(t('tooLargeFile'))
            return
        }

        let data = new FormData()
        const filenameNotUnique = allFiles.some(obj => obj.filename === file.name)
        const fileName = filenameNotUnique ? `${moment().format('YYYY-MM-DD_HH-mm-ss')}_${file.name}` : file.name
        const fileType = file.type
        const fileSize = file.size
        const chunkSize = 15 * 1024 * 1024 // 15MB
        let fileChunk = null, chunkNb = 0

        for (let start = 0; start < fileSize; start += chunkSize, chunkNb++) {
            if (start + chunkSize > fileSize) { fileChunk = file.slice(start, fileSize) }
            else { fileChunk = file.slice(start, start + chunkSize) }
            data.append('fileChunks', fileChunk, fileName)
        }
        data.append('fileType', fileType)
        data.append('fileSize', fileSize)
        data.append('chunkNb', chunkNb)

        const options = {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent
                let percent = Math.floor( (loaded * 100) / total )
                this.setState(prevState => ({
                    uploadState: {
                        ...prevState.uploadState,
                        [file.name]: percent
                    }
                }))
            }
        }

        try {
            const res = await axios.post(`/${this.props.url}`, data, options)
            this.handleResponse(res.data, t)
            toastr.success(t('successFileUploading'))
        } catch (err) {
            const data = _.get(err, 'response.data', {})
            toastr.error(t(data.message || 'errorUploading', data))
        } finally {
            this.setState(prevState => {
                const progress = { ...prevState.uploadState }
                delete progress[file.name]
                return { uploadState: progress }
            })
        }
    }

    handleFilesUpload = (t, allFiles) => async (event) => {
        const files = Array.from(event.target.files) // copy the FileList early
        event.target.value = null // reset to null to allow re-uploading the same files

        for (let i = 0; i < files.length; i++) {
            await this.handleOneFileUpload(files[i], t, allFiles)
        }
    }

    findRowIndex = (rowId) => {
        const { allIds } = this.props

        return allIds.findIndex(id => id === rowId)
    }

    renderDownloadAndPreview = (file, language, groupModel, accessId, files, showDeleteIcon) => {
        return <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '6px'}}>
            <FilePreviewAndDownload
                key={file.id}
                file={file}
                language = {language}
                groupModel={groupModel}
                accessId={accessId}
                files={files}
                url={this.props.url}
            />
            {showDeleteIcon && <TrashCell
                onClick={() => {
                const index = this.findRowIndex(file.id)
                this.props.remove(index)
            }}/>}
        </div>
    }

    render() {
        const { allIds = [], byId = {}, t = defaultT, language = 'fr', placeholder, tKey, noRowsMessage, allowedFormats, editable, readOnly, displayEmpty, anonymous, removable, user, groupModel, accessId, style } = this.props

        const sortedIds = this.state.sortBy
            ? sort(allIds, id => byId[id][this.state.sortBy], this.state.sortDirection)
            : allIds

        const objects = sortedIds.map(id => byId[id])

        const showDeleteIcon = editable && removable

        const accept = Array.isArray(allowedFormats) && allowedFormats.length > 0
            ? allowedFormats.join(',')
            : undefined // accept all types


        const columns = [<Column
            flexGrow={1}
            width={200}
            dataKey="filename"
            label={t(tKey, { capitalize: true })}
            className='Files-DataTable-standard-column'
        />]

        if(!readOnly && !anonymous) {
            columns.push(<Column
                flexGrow={0}
                width={220}
                dataKey="user.name"
                label={t('user', { capitalize: true })}
                cellDataGetter={({ dataKey, rowData }) => _.get(rowData, dataKey)}
                className='Files-DataTable-standard-column'
            />)
        }

        if(!readOnly) {
            columns.push(<Column
                flexGrow={0}
                width={160}
                dataKey="date"
                label={t('date', { capitalize: true })}
                className='Files-DataTable-standard-column'
            />)
        }

        return (
            <div className="Files-container" style={style}>
                <div className="Files-panel-body">
                    <AutoSizer disableHeight>
                        {({ width }) => (
                            <Table
                                objects={objects}
                                sort={({ sortBy, sortDirection }) => {
                                    this.setState({
                                        sortBy,
                                        sortDirection
                                    })
                                }}
                                sortBy={this.state.sortBy}
                                sortDirection={this.state.sortDirection}
                                headerRowClassName="AccordionHeaderRow"
                                maxRows={7}
                                width={width}
                                noRowsLabel={t(noRowsMessage || 'noRows')}
                            >
                                {columns}
                                <Column
                                    flexGrow={0}
                                    flexShrink={0}
                                    width={showDeleteIcon ? 80 : 60} //different width depending on if there is a delete icon also or not
                                    dataKey="id"
                                    label=""
                                    cellRenderer={({rowData}) => this.renderDownloadAndPreview(rowData, language, groupModel, accessId, objects, showDeleteIcon)}
                                    className='Files-DataTable-standard-column'
                                    //className='DataTable-Table-row-column'
                                />
                                {/*
                                    showDeleteIcon && <Column
                                        flexGrow={0}
                                        flexShrink={0}
                                        width={40}
                                        dataKey="id"
                                        label=""
                                        cellRenderer={this.renderDeleteIcon}
                                        className='Files-DataTable-standard-column'
                                        //className='DataTable-Table-row-column'
                                    />
                                */}
                            </Table>
                        )}
                    </AutoSizer>
                </div>
                {
                    editable && <div
                        className="Files-input-group"
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); this.setState({ isDragging: true }); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) this.setState({ isDragging: false }); }}
                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); this.setState({ isDragging: false }); this.handleFilesUpload(t, objects)({ target: { files: e.dataTransfer.files } }); }}
                    >
                        <button
                            className={`btn Files-button${this.state.isDragging ? ' drag-over' : ''}${this.state.hovered ? ' hovered' : ''}`}
                            style={style}
                        >
                            <Upload size={16} className="Files-Icon" />
                            <span className="Files-button-Text">
                                {t(placeholder || 'addFile(s)', { capitalize: true })}
                            </span>
                        </button>
                        <input
                            className="Files-input"
                            type="file"
                            accept={accept}
                            multiple
                            encType='multipart/form-data'
                            onMouseEnter={() => this.setState({hovered: true})}
                            onMouseLeave={() => this.setState({hovered: false})}
                            onDragOver={() => this.setState({hovered: true})}
                            onChange={this.handleFilesUpload(t, objects)}
                        />
                    </div>
                }
                {editable && <UploadHint kind="file" formats={allowedFormats} t={t} />}
                {Object.keys(this.state.uploadState).length > 0 && (
                    <div className="upload-progress-container">
                        {Object.entries(this.state.uploadState).map(([filename, percent]) => (
                            <div key={filename} className="upload-progress-item">
                                <div className="upload-progress-info">
                                    <span className="upload-filename">{filename}</span>
                                    <span className="upload-percentage">{percent === 100 ? t('processingFile') : `${percent}%`}</span>
                                </div>
                                <ProgressBar percentage={percent} color='#2ecc71'/>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    }
}

Files.propTypes = {
    push: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
    t: PropTypes.func
}

export default Files
